import { useEffect, useState } from "react";
import { api, formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Archive, ArchiveRestore, Repeat } from "lucide-react";
import { STATUSES, inputCls } from "../constants";
import { ClientPicker } from "../clients/ClientPicker";

export const lbl = "text-[11px] font-mono uppercase tracking-[0.1em] text-[#475467]";

export function Field({ label, children }) {
  return <div><Label className={lbl}>{label}</Label>{children}</div>;
}

export function JobOverview({ job, photographers, onChanged, onClose }) {
  const [form, setForm] = useState({
    status: job.status, photographer_id: job.photographer_id || "", scheduled_date: job.scheduled_date || "", scheduled_time: job.scheduled_time || "",
    contact: { name: "", email: "", phone: "", ...job.contact },
    address: { street: "", city: "", state: "", zip: "", ...job.address },
    meeting: { contact_name: "", contact_phone: "", access: "", notes: "", ...(job.meeting || {}) },
    sqft: String(job.sqft || ""),
  });
  const [saving, setSaving] = useState(false);
  const [estimate, setEstimate] = useState(job.quote);
  const [reassignOpen, setReassignOpen] = useState(false);
  const [reassigning, setReassigning] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setIn = (grp, k) => (e) => setForm((f) => ({ ...f, [grp]: { ...f[grp], [k]: e.target.value } }));
  useEffect(() => {
    const sqft = Number(form.sqft || 0);
    if (!sqft) return;
    api.post("/pricing/quote", { services: job.services || [], sqft, virtual_staging: job.virtual_staging || null, twilight_option: job.twilight_option || null, coupon_code: job.quote?.coupon?.code || null }).then((r) => setEstimate(r.data)).catch(() => {});
  }, [form.sqft, job]);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/jobs/${job.id}`, { ...form, sqft: Number(form.sqft || 0), photographer_id: form.photographer_id || null, scheduled_date: form.scheduled_date || null, scheduled_time: form.scheduled_time || null });
      toast.success("Job updated"); onChanged();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); } finally { setSaving(false); }
  };
  const archive = async () => {
    if (!window.confirm(`${job.archived ? "Restore" : "Archive"} this order? It remains in your audit records.`)) return;
    await api.post(`/admin/jobs/${job.id}/${job.archived ? "restore" : "archive"}`);
    toast.success(job.archived ? "Job restored" : "Job archived — find it under “Archived”");
    onChanged(); if (!job.archived) onClose();
  };
  const cancel = async () => {
    if (!window.confirm("Cancel this photoshoot? This action keeps the order and invoice history for audit purposes.")) return;
    try { await api.patch(`/admin/jobs/${job.id}`, { status: "cancelled" }); toast.success("Order cancelled"); onChanged(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  const reassignClient = async (client) => {
    const currentName = form.contact.name || "this contact";
    if (!window.confirm(`Reassign this order from "${currentName}" to "${client.name}"? Both clients' order histories and totals will update accordingly.`)) return;
    setReassigning(true);
    try {
      const { data } = await api.post(`/admin/jobs/${job.id}/reassign-client`, { client_id: client.id });
      setForm((f) => ({ ...f, contact: { name: data.contact?.name || "", email: data.contact?.email || "", phone: data.contact?.phone || "" } }));
      toast.success(`Order reassigned to ${client.name}`);
      setReassignOpen(false);
      onChanged();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); } finally { setReassigning(false); }
  };

  return (
    <div className="space-y-8">
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Field label="Status">
          <Select value={form.status} onValueChange={(v) => set("status", v)}>
            <SelectTrigger data-testid="detail-status" className={inputCls}><SelectValue /></SelectTrigger>
            <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
        <Field label="Photographer">
          <Select value={form.photographer_id || "none"} onValueChange={(v) => set("photographer_id", v === "none" ? "" : v)}>
            <SelectTrigger data-testid="detail-photographer" className={inputCls}><SelectValue placeholder="Assign" /></SelectTrigger>
            <SelectContent><SelectItem value="none">Unassigned</SelectItem>{photographers.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
          </Select>
          {Object.keys(job.service_assignments || {}).length > 0 && <div data-testid="service-assignments-hint" className="mt-1.5 text-xs text-[#94A3B8]">{Object.keys(job.service_assignments).length} service{Object.keys(job.service_assignments).length === 1 ? "" : "s"} assigned to a different shooter — see Services tab</div>}
        </Field>
        <Field label="Date"><Input data-testid="job-date" type="date" value={form.scheduled_date} onChange={(e) => set("scheduled_date", e.target.value)} className={inputCls} /></Field>
        <Field label="Time"><Input data-testid="job-time" type="time" value={form.scheduled_time} onChange={(e) => set("scheduled_time", e.target.value)} className={inputCls} /></Field>
      </div>

      <div className="grid lg:grid-cols-2 gap-8">
        <div className="space-y-4">
          <div className="flex items-center justify-between gap-3">
            <h3 className="font-serif-display text-xl text-[#101828]">Client</h3>
            <button type="button" data-testid="job-reassign-client-toggle" onClick={() => setReassignOpen((v) => !v)} className="inline-flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-[0.1em] text-[#475467] hover:text-[#0C2347] transition-colors">
              <Repeat className="h-3.5 w-3.5" strokeWidth={1.5} /> {reassignOpen ? "Cancel" : "Reassign client"}
            </button>
          </div>
          {reassignOpen && (
            <div data-testid="job-reassign-client-picker" className={`rounded-xl border border-dashed border-[#E4E9F0] bg-[#F7F9FC] p-3 ${reassigning ? "opacity-50 pointer-events-none" : ""}`}>
              <p className="text-xs text-[#475467] mb-2">Search for an existing client to move this order to. Their contact details will replace the ones below, and order histories for both clients will update.</p>
              <ClientPicker selected={null} onSelect={reassignClient} onClear={() => setReassignOpen(false)} />
            </div>
          )}
          <Field label="Name"><Input data-testid="job-contact-name" value={form.contact.name} onChange={setIn("contact", "name")} className={inputCls} /></Field>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Email"><Input data-testid="job-contact-email" value={form.contact.email} onChange={setIn("contact", "email")} className={inputCls} /></Field>
            <Field label="Phone"><Input data-testid="job-contact-phone" value={form.contact.phone} onChange={setIn("contact", "phone")} className={inputCls} /></Field>
          </div>
          <h3 className="font-serif-display text-xl text-[#101828] pt-2">On-site</h3>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Contact name"><Input value={form.meeting.contact_name} onChange={setIn("meeting", "contact_name")} className={inputCls} /></Field>
            <Field label="Contact phone"><Input value={form.meeting.contact_phone} onChange={setIn("meeting", "contact_phone")} className={inputCls} /></Field>
          </div>
          <Field label="Access"><Input value={form.meeting.access} onChange={setIn("meeting", "access")} className={inputCls} /></Field>
          <Field label="Notes"><Textarea value={form.meeting.notes} onChange={setIn("meeting", "notes")} rows={2} className={inputCls} /></Field>
        </div>
        <div className="space-y-4">
          <h3 className="font-serif-display text-xl text-[#101828]">Listing address</h3>
          <Field label="Street"><Input data-testid="job-address-street" value={form.address.street} onChange={setIn("address", "street")} className={inputCls} /></Field>
          <div className="grid grid-cols-3 gap-4">
            <Field label="City"><Input data-testid="job-address-city" value={form.address.city} onChange={setIn("address", "city")} className={inputCls} /></Field>
            <Field label="State"><Input value={form.address.state} onChange={setIn("address", "state")} className={inputCls} /></Field>
            <Field label="ZIP"><Input value={form.address.zip} onChange={setIn("address", "zip")} className={inputCls} /></Field>
          </div>
          <div className="grid grid-cols-[1fr_auto] gap-4 items-end"><Field label="Square footage"><Input data-testid="job-sqft-input" type="number" min="1" value={form.sqft} onChange={(e) => set("sqft", e.target.value)} className={inputCls} /></Field><div data-testid="job-overview-estimated-total" className="rounded-xl border border-[#E4E9F0] px-4 py-2.5 text-right"><div className="eyebrow">Live estimate</div><div className="font-mono text-lg mt-1 text-[#101828]">${estimate?.total ?? "—"}</div></div></div>
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-4 border-t border-[#E4E9F0] pt-6">
        <div className="flex gap-2"><Button data-testid="job-archive" variant="outline" onClick={archive} className="rounded-xl border-[#E4E9F0] text-[#475467] font-mono text-[11px] uppercase tracking-[0.1em]">
          {job.archived ? <ArchiveRestore className="h-4 w-4 mr-2" strokeWidth={1.5} /> : <Archive className="h-4 w-4 mr-2" strokeWidth={1.5} />}{job.archived ? "Restore order" : "Archive order"}
        </Button>{job.status !== "cancelled" && <Button data-testid="job-cancel" variant="outline" onClick={cancel} className="rounded-xl border-[#F3D9D7] text-[#B3261E] hover:bg-[#FBEAE9]">Cancel order</Button>}</div>
        <Button data-testid="detail-save" disabled={saving} onClick={save} className="rounded-xl bg-[#0C2347] hover:bg-[#123262] text-white px-8">{saving ? "Saving…" : "Save changes"}</Button>
      </div>
    </div>
  );
}
